import {
	createContext,
	useCallback,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
	CURP_EXTRACTION_TERMINAL_STATUSES,
	curpExtractionQueryKey,
	getCurpExtractionService,
	triggerCurpExtractionService,
	type CurpExtractionState,
} from '../../../shared/services/curpExtraction.service';
import { hasExtractionEvidence } from '../../../shared/fiscal/lib/extractionEvidence';
import {
	applyNewerEvidence,
	evidenceAt,
	isStaleRetrying,
	type NewerEvidenceRequirement,
} from '../../../shared/fiscal/lib/freshEvidence';
import { logger } from '../../../shared/utils/logger';

// ─────────────────────────────────────────────────────────────────────────────
// Estado compartido del banner de extracción CURP.
//
// Hay UN solo punto que DISPARA la extracción: la acción explícita del user al
// generar su RFC desde el CURP (`triggerExtraction`, llamado en ConfirmRfcStep).
// Todo lo demás solo OBSERVA (`observeExtraction`): setea el CURP activo para
// que corra el polling-GET y el banner se rehidrate desde lo que el backend ya
// tenga. El resume al montar (useResumeCurpExtraction) usa SOLO observe — nunca
// re-dispara, así que un user que regresa horas después no genera un workflow
// de extracción nuevo e innecesario (la idempotencia del backend es de 10 min).
//
// Polling mientras el status es pending o running, o un `stale` que todavía es
// reintento (isStaleRetrying: el workflow reintenta el nombre a los 5, 10 y
// 20 min sin escribir la fila; t-324, 23-oct: ~55% de las corridas terminan
// después de un `stale`). Cada POLL_INTERVAL_MS los primeros
// POLL_FAST_WINDOW_MS y luego cada POLL_SLOW_INTERVAL_MS hasta
// POLL_DEADLINE_MS. Al llegar a done/failed, a un `stale` real o al vencer
// POLL_DEADLINE_MS, para de pollear.
// ─────────────────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 2_500;
const POLL_FAST_WINDOW_MS = 2 * 60_000;
const POLL_SLOW_INTERVAL_MS = 15_000;
// Tope de seguridad: si el backend se queda en pending/running, dejamos de
// pollear a los 40 min (la ventana de reintentos). Volver a la pestaña o a la
// app refetchea igual (staleTime 0), así que quien regresa después ve el
// resultado.
const POLL_DEADLINE_MS = 40 * 60_000;

/** Desde dónde se disparó la extracción. Viaja hasta los eventos: sin esto,
 * TODA extracción se atribuía al drawer de vinculación (el hook de tracking
 * tenía 'consult_rfc' hardcodeado) y no había forma de saber cuántas trajo el
 * nodo de CURP del onboarding. */
export type CurpExtractionSurface = 'consult_rfc' | 'onboarding_curp' | 'plans_quiz';

export type CurpExtractionCtx = {
	activeCurp: string | null;
	/** Superficie del disparo VIGENTE (null mientras solo se observa). */
	surface: CurpExtractionSurface | null;
	state: CurpExtractionState | null;
	isFetching: boolean;
	// True cuando el `activeCurp` fue disparado (POST) en esta sesión. Permite
	// que el banner muestre "analizando" de inmediato en el flujo de trigger,
	// mientras que el flujo de observe solo reacciona si el GET trae evidencia
	// real de una extracción.
	hasTriggered: boolean;
	// Solo lee: setea el CURP activo para correr el polling-GET. No dispara nada.
	observeExtraction: (curp: string) => void;
	// Dispara la extracción (POST, idempotente por CURP en sesión) + observa.
	triggerExtraction: (
		curp: string,
		surface: CurpExtractionSurface,
	) => Promise<void>;
	/** Llamar justo antes de disparar otra validación del CURP: lee la
	 * evidencia vigente y, mientras no llegue una más nueva, `state` reporta
	 * `running` y el polling sigue (ver freshEvidence.ts). */
	expectNewerEvidence: (curp: string) => Promise<void>;
	/** Suelta la espera (p. ej. el disparo falló). */
	clearNewerEvidence: () => void;
};

export const CurpExtractionContext = createContext<CurpExtractionCtx | null>(null);

export const CurpExtractionProvider = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	const [activeCurp, setActiveCurp] = useState<string | null>(null);
	const [surface, setSurface] = useState<CurpExtractionSurface | null>(null);
	const [newerEvidence, setNewerEvidence] =
		useState<NewerEvidenceRequirement | null>(null);
	const queryClient = useQueryClient();

	// CURPs ya disparados en esta sesión. Garantiza que llamar `startExtraction`
	// del mismo CURP múltiples veces (p.ej. desde un useEffect en OnboardingLayout
	// que monta en cada step) sólo dispare UN POST por CURP por sesión. El
	// backend tiene su propia idempotency cache de 10min pero la nuestra evita
	// hasta la llamada HTTP.
	const triggeredCurpsRef = useRef<Set<string>>(new Set());

	// Ancla wall-clock del polling por CURP. Se setea desde los handlers (no en
	// un effect) y solo se reinicia cuando cambia el CURP activo, para acotar el
	// loop de refetch a POLL_DEADLINE_MS.
	const pollStartedAtRef = useRef<{ curp: string; at: number } | null>(null);
	const markPollStart = useCallback((curp: string) => {
		if (pollStartedAtRef.current?.curp !== curp) {
			pollStartedAtRef.current = { curp, at: Date.now() };
		}
	}, []);

	const query = useQuery<CurpExtractionState>({
		queryKey: curpExtractionQueryKey(activeCurp),
		queryFn: () => getCurpExtractionService(activeCurp as string),
		enabled: !!activeCurp,
		refetchInterval: (q) => {
			const now = Date.now();
			const observed = applyNewerEvidence(q.state.data ?? null, newerEvidence, now);
			const status = observed?.status;
			const startedAt = pollStartedAtRef.current?.at;
			const elapsed = startedAt != null ? now - startedAt : 0;
			if (
				status &&
				CURP_EXTRACTION_TERMINAL_STATUSES.includes(status) &&
				!isStaleRetrying(observed, now)
			)
				return false;
			if (elapsed >= POLL_DEADLINE_MS) {
				logger.warn('[curp-extraction] polling deadline reached', {
					curp: activeCurp,
					lastStatus: status ?? null,
				});
				return false;
			}
			return elapsed < POLL_FAST_WINDOW_MS ? POLL_INTERVAL_MS : POLL_SLOW_INTERVAL_MS;
		},
		// El backend siempre responde 200, incluso sin filas — no queremos retries
		// agresivos que se acumulen en background.
		retry: 1,
		staleTime: 0,
	});

	const state = useMemo(
		() => applyNewerEvidence(query.data ?? null, newerEvidence, Date.now()),
		[query.data, newerEvidence],
	);

	// Solo observa: habilita el polling-GET para el CURP. No hace POST, así que
	// es seguro llamarlo en cada mount/reload sin generar trabajo en el backend.
	const observeExtraction = useCallback((curp: string) => {
		markPollStart(curp);
		setActiveCurp((prev) => (prev === curp ? prev : curp));
	}, [markPollStart]);

	// Asegura que exista una extracción para el CURP + observa. La idempotencia
	// se basa en el ESTADO REAL (GET), no en un flag en memoria: si el GET ya
	// trae evidencia de una extracción (en curso o terminada) NO re-disparamos —
	// solo observamos. Solo hacemos POST cuando no existe ninguna. Así, un user
	// que regresa en otra sesión con data ya extraída no genera un workflow
	// nuevo e innecesario (aunque haya expirado la ventana de 10 min del backend).
	//
	// El `triggeredCurpsRef` se mantiene como guard de sesión para evitar GETs/
	// POSTs redundantes ante llamadas rápidas con el mismo CURP.
	const triggerExtraction = useCallback(async (
		curp: string,
		origin: CurpExtractionSurface,
	) => {
		markPollStart(curp);
		setActiveCurp(curp);
		setSurface(origin);
		if (triggeredCurpsRef.current.has(curp)) return;
		triggeredCurpsRef.current.add(curp);
		try {
			const existing = await getCurpExtractionService(curp);
			if (hasExtractionEvidence(existing)) return;
			await triggerCurpExtractionService(curp);
		} catch (err) {
			triggeredCurpsRef.current.delete(curp);
			logger.error('[curp-extraction] trigger failed', err);
		}
	}, [markPollStart]);

	const expectNewerEvidence = useCallback(async (curp: string) => {
		const current = await getCurpExtractionService(curp).catch(() => null);
		const baselineEvidenceAt = current ? evidenceAt(current) : null;
		if (!baselineEvidenceAt) return;
		pollStartedAtRef.current = { curp, at: Date.now() };
		setActiveCurp(curp);
		setNewerEvidence({ curp, baselineEvidenceAt, requiredSince: Date.now() });
		await queryClient.invalidateQueries({ queryKey: curpExtractionQueryKey(curp) });
	}, [queryClient]);

	const clearNewerEvidence = useCallback(() => setNewerEvidence(null), []);

	const hasTriggered =
		activeCurp != null && triggeredCurpsRef.current.has(activeCurp);

	const value = useMemo<CurpExtractionCtx>(
		() => ({
			activeCurp,
			surface,
			state,
			isFetching: query.isFetching,
			hasTriggered,
			observeExtraction,
			triggerExtraction,
			expectNewerEvidence,
			clearNewerEvidence,
		}),
		[
			activeCurp,
			surface,
			state,
			query.isFetching,
			hasTriggered,
			observeExtraction,
			triggerExtraction,
			expectNewerEvidence,
			clearNewerEvidence,
		],
	);

	return (
		<CurpExtractionContext.Provider value={value}>
			{children}
		</CurpExtractionContext.Provider>
	);
};
